import { Controller } from "@hotwired/stimulus";

// Tests employer names against the whole VoE DNC list, the way a verification is checked: entries
// for all companies (global) plus, when one is picked, that company's own. Matching happens server-side
// with the gate's rule. Results are a scratch list — nothing is saved.
//
// Usage (on the panel wrapper):
//   data-controller="support--dnc-test-configuration"
//   data-support--dnc-test-configuration-url-value="<test configuration path>"
export default class extends Controller {
  static targets = ["name", "company", "results", "clear"];
  static values = { url: String };

  connect() {
    this.names = [];
    this.results = new Map();
    this.failed = false;
    this.render();
  }

  disconnect() {
    this.request?.abort();
  }

  add() {
    const name = this.nameTarget.value.trim();
    this.nameTarget.value = "";
    if (!name || this.names.includes(name)) return;

    this.names.push(name);
    this.render();
    this.check();
  }

  remove(event) {
    this.names.splice(Number(event.currentTarget.dataset.index), 1);
    this.render();
  }

  clear() {
    this.request?.abort();
    this.names = [];
    this.results = new Map();
    this.failed = false;
    this.render();
  }

  // Re-checks every name, e.g. after the company changes.
  async check() {
    if (this.names.length === 0) return;

    this.request?.abort();
    this.request = new AbortController();
    this.results = new Map();
    this.failed = false;
    this.render();

    const params = new URLSearchParams({ company_id: this.companyTarget.value });
    this.names.forEach((name) => params.append("names[]", name));

    try {
      const response = await fetch(`${this.urlValue}?${params}`, {
        headers: { Accept: "application/json" },
        signal: this.request.signal,
      });
      if (!response.ok) throw new Error(`Test failed: ${response.status}`);

      // An expired session redirects to the sign-in page, so json() throws here too.
      const body = await response.json();
      this.results = new Map(body.results.map((result) => [result.name, result]));
    } catch (error) {
      if (error.name === "AbortError") return;

      // Without this every row would sit on "checking" forever, with no hint why.
      this.results = new Map();
      this.failed = true;
    }
    this.render();
  }

  render() {
    this.resultsTarget.replaceChildren(...this.names.map((name, index) => this.row(name, index)));
    this.resultsTarget.classList.toggle("d-none", this.names.length === 0);
    this.clearTarget.classList.toggle("d-none", this.names.length === 0);
  }

  row(name, index) {
    const result = this.results.get(name);

    const item = document.createElement("li");
    item.className = "list-group-item d-flex align-items-center gap-2";
    // A row with no result is either still checking or part of a request that failed.
    const state = result ? String(result.caught) : this.failed ? "error" : "pending";
    item.dataset.caught = state;

    const icon = document.createElement("span");
    icon.textContent = { true: "✅", false: "❌", error: "⚠️", pending: "…" }[state];

    const label = document.createElement("span");
    label.className = "fw-semibold";
    label.textContent = name;

    const detail = document.createElement("span");
    detail.className = "text-body-secondary";
    detail.textContent = result
      ? `— ${this.describe(result)}`
      : this.failed
        ? "— couldn't check. Refresh the page and try again."
        : "— checking";

    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "btn-close ms-auto";
    remove.ariaLabel = `Remove ${name}`;
    remove.dataset.index = index;
    remove.dataset.action = "support--dnc-test-configuration#remove";

    item.append(icon, label, detail, remove);
    return item;
  }

  describe(result) {
    if (!result.caught) return "not caught";

    const entries = result.entries.map(
      (entry) => `"${entry.value}" (${entry.company ? `${entry.company} only` : "global"})`,
    );
    return `caught by ${entries.join(", ")}`;
  }
}
